import { usePermissions } from '../../hooks/usePermissions'
import { AlertCircle, Lock } from 'lucide-react'

/**
 * Component to conditionally render content based on permissions
 * @param {Object} props - Component props
 * @param {string} props.permission - Single permission to check
 * @param {Array<string>} props.permissions - Array of permissions (any)
 * @param {Array<string>} props.allPermissions - Array of permissions (all required)
 * @param {React.ReactNode} props.children - Content to render if permission granted
 * @param {React.ReactNode} props.fallback - Content to render if permission denied
 * @param {boolean} props.showFallback - Whether to show fallback content
 * @param {string} props.fallbackType - Type of fallback ('hidden', 'disabled', 'message')
 * @param {string} props.fallbackMessage - Custom fallback message
 * @returns {React.ReactNode} - Rendered content
 */
const PermissionGate = ({
  permission,
  permissions,
  allPermissions,
  children,
  fallback,
  showFallback = true,
  fallbackType = 'hidden',
  fallbackMessage
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading, metadata } = usePermissions()

  // While loading, show children (optimistic rendering)
  if (isLoading) {
    return children
  }

  let hasAccess = false

  // Check permissions based on props
  if (permission) {
    hasAccess = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    hasAccess = hasAnyPermission(permissions)
  } else if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAllPermissions(allPermissions)
  } else {
    // No permissions specified, grant access
    hasAccess = true
  }

  // If access granted, render children
  if (hasAccess) {
    return children
  }

  // If no fallback should be shown, return null
  if (!showFallback) {
    return null
  }

  // If custom fallback provided, use it
  if (fallback) {
    return fallback
  }

  // Generate default fallback based on type
  switch (fallbackType) {
    case 'hidden':
      return null

    case 'disabled':
      return (
        <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
          {children}
        </div>
      )

    case 'message':
      const permissionName = permission
        ? metadata?.[permission]?.name || permission
        : permissions?.map(p => metadata?.[p]?.name || p).join(' or ')
        || allPermissions?.map(p => metadata?.[p]?.name || p).join(' and ')
        || 'required permissions'

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          background: 'var(--bg-1)',
          borderRadius: 'var(--radius)',
          border: '2px dashed var(--border)',
        }}>
          <div style={{ textAlign: 'center' }}>
            <Lock style={{ width: '2rem', height: '2rem', color: 'var(--text-faint)', margin: '0 auto 0.5rem' }} />
            <p className="font-medium text-muted" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
              Access Restricted
            </p>
            <p className="text-faint" style={{ fontSize: '0.75rem' }}>
              {fallbackMessage || `Requires: ${permissionName}`}
            </p>
          </div>
        </div>
      )

    default:
      return null
  }
}

/**
 * Higher-order component to wrap components with permission checking
 * @param {React.Component} Component - Component to wrap
 * @param {Object} permissionConfig - Permission configuration
 * @returns {React.Component} - Wrapped component
 */
export const withPermission = (Component, permissionConfig) => {
  return (props) => (
    <PermissionGate {...permissionConfig}>
      <Component {...props} />
    </PermissionGate>
  )
}

/**
 * Component to show permission-based error message
 * @param {Object} props - Component props
 * @param {string} props.permission - Permission that was required
 * @param {string} props.message - Custom error message
 * @returns {React.ReactNode} - Error message component
 */
export const PermissionDenied = ({ permission, message }) => {
  const { metadata } = usePermissions()

  const permissionName = metadata?.[permission]?.name || permission
  const permissionDescription = metadata?.[permission]?.description

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
      <div style={{ textAlign: 'center', maxWidth: '28rem', margin: '0 auto' }}>
        <AlertCircle style={{ width: '4rem', height: '4rem', color: 'var(--danger)', margin: '0 auto 1rem' }} />
        <h3 className="font-medium" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
          Access Denied
        </h3>
        <p className="text-muted" style={{ marginBottom: '1rem' }}>
          {message || `You don't have permission to access this feature.`}
        </p>
        {permissionName && (
          <div style={{
            background: 'var(--bg-1)',
            borderRadius: 'var(--radius)',
            padding: '1rem',
          }}>
            <p className="font-medium" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
              Required Permission: {permissionName}
            </p>
            {permissionDescription && (
              <p className="text-faint" style={{ fontSize: '0.75rem' }}>
                {permissionDescription}
              </p>
            )}
          </div>
        )}
        <p className="text-faint" style={{ fontSize: '0.75rem', marginTop: '1rem' }}>
          Contact your administrator if you believe this is an error.
        </p>
      </div>
    </div>
  )
}

export default PermissionGate
